import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, type CardCheckResult, type CardHeldRow } from '../lib/api';
import { ErrorBox, dash, money } from '../components/DataTable';
import { canScreen, useMe } from '../lib/me';
import { kindLabel } from '../lib/driverKind';
import { FitBox } from '../components/FitBox';
import { hitAny, searchWords } from '../lib/search';

/**
 * 회계 › 카드 점검(2026-10-20 팀장 전체회의 #39 「몇 호 차에 어떤 카드가 있는지 매칭 · 영업↔회계 크로스 체크」). 서버 `functions/src/cardCheck.ts`.
 *
 * - 정리할 카드: 끝난 편성이 아직 쥔 주유·하이패스 번호 → 회수 처리(번호 비움) / 같은 차량 새 편성으로 넘기기
 * - 사용 대조: 카드사 청구(재무추가 카드대량등록) 줄 중 그 달 편성과 안 맞는 줄
 * - 나가 있는 카드: 운행 중 편성의 카드 목록
 */

type View = 'held' | 'usage' | 'live';
const KIND: Record<'fuel' | 'hipass', string> = { fuel: '주유카드', hipass: '하이패스' };
const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));
const btn = 'rounded border border-line px-1.5 py-0.5 text-xs hover:bg-canvas disabled:opacity-40';

function heldState(r: CardHeldRow): { label: string; tone: string } {
  if (r.move_to) return { label: `같은 차량 새 편성(${dash(r.move_to_driver)})에 ${KIND[r.kind]} 없음`, tone: 'text-amber-800' };
  if (r.live_cnt === 0) return { label: '차량 운행 끝 — 회수·해지 대상', tone: 'text-red-700' };
  return { label: '새 편성에도 카드 있음 — 옛 번호', tone: 'text-ink-soft' };
}

export function CardCheckTab() {
  const { me } = useMe();
  const canWrite = canScreen(me, 'accounting', 'write') || canScreen(me, 'master', 'write');
  const [data, setData] = useState<CardCheckResult | null>(null);
  const [mon, setMon] = useState<string | null>(null);
  const [view, setView] = useState<View>('held');
  const [term, setTerm] = useState('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setErr('');
    try { setData(await api.listCardCheck({ mon })); } catch (e) { setErr(errMsg(e)); }
  }, [mon]);
  useEffect(() => { void load(); }, [load]);

  // #419 띄어쓰기 낱말 중 하나라도(OR) — `lib/search.ts`.
  const q = term.trim().toLowerCase();
  const words = useMemo(() => searchWords(q), [q]);
  const hit = (...v: (string | null | undefined)[]) => hitAny(words, v);
  const held = useMemo(() => (data?.held ?? []).filter((r) => hit(r.car_no, r.driver_name, r.owner_name, r.grp_name, r.num)), [data, q]);
  const usage = useMemo(() => (data?.usage ?? []).filter((r) => hit(r.car_no, r.driver_name, r.owner_name, r.account_name)), [data, q]);
  const live = useMemo(() => (data?.live ?? []).filter((r) => hit(r.car_no, r.driver_name, r.owner_name, r.grp_name, r.fuel_num, r.hipass_num)), [data, q]);

  async function release(r: CardHeldRow, moveTo: number | null) {
    const what = `${dash(r.car_no)} ${dash(r.driver_name)} ${KIND[r.kind]} ${r.num}`;
    const ask = moveTo
      ? `${what}\n이 번호를 같은 차량 새 편성(${dash(r.move_to_driver)})으로 넘길까요?`
      : `${what}\n이 번호를 끝난 편성에서 지울까요?(회수·해지한 카드)`;
    if (!window.confirm(ask)) return;
    const key = `${r.car_alloc_id}-${r.kind}`;
    setBusy(key); setErr(''); setMsg('');
    try {
      await api.releaseAllocCard({ carAllocId: r.car_alloc_id, kind: r.kind, moveTo });
      setMsg(moveTo ? `${what} → 새 편성으로 넘겼습니다.` : `${what} 회수 처리했습니다.`);
      await load();
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(null); }
  }

  const counts = {
    move: (data?.held ?? []).filter((r) => r.move_to).length,
    ended: (data?.held ?? []).filter((r) => !r.move_to && r.live_cnt === 0).length,
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1 text-sm">
        {([['held', `정리할 카드 ${data?.held.length ?? ''}`], ['usage', `사용 대조 ${data?.usage.length ?? ''}`], ['live', `나가 있는 카드 ${data?.live.length ?? ''}`]] as [View, string][]).map(([id, label]) => (
          <button key={id} onClick={() => setView(id)}
                  className={`rounded-lg px-2.5 py-1 ${view === id ? 'bg-brand-600 font-semibold text-white shadow-sm' : 'text-ink hover:bg-brand-50'}`}>{label}</button>
        ))}
        {view === 'usage' && data && (
          <select className="field ml-1 w-32" aria-label="정산월" title="카드 청구 정산월" value={data.mon ?? ''}
                  onChange={(e) => setMon(e.target.value || null)}>
            {data.months.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        )}
        <input className="field ml-auto w-56" placeholder="차량번호·기사·차주·카드번호" value={term} onChange={(e) => setTerm(e.target.value)} />
      </div>
      <p className="page-hint px-1">
        {view === 'held' && <>편성이 끝났는데 카드 번호가 그대로 남은 줄입니다. 카드를 회수·해지했으면 <b>회수 처리</b>, 같은 차량에 새로 선 편성이 그 카드를 쓰면 <b>새 편성으로 넘기기</b>를 누르세요. 넘길 곳 {counts.move} · 차량 운행 끝 {counts.ended}.</>}
        {view === 'usage' && <>카드사 청구(재무추가 카드 대량등록) {data?.usageTotal ?? 0}줄 중 그 달 편성과 맞지 않는 줄입니다. 편성에 카드 번호가 없으면 기준정보 › 운행차량편성의 카드 정보에 넣어 주세요.</>}
        {view === 'live' && <>운행 중 편성에 등록된 카드입니다. 현장 카드 목록과 대조할 때 쓰세요.</>}
      </p>
      {err && <ErrorBox message={err} />}
      {msg && <div className="card px-3 py-2 text-sm text-emerald-800">{msg}</div>}
      <FitBox deps={[data, view, q, err, msg]}>
        {view === 'held' && (
          <table className="w-full text-sm">
            <thead className="sticky top-0">
              <tr className="text-left text-xs text-ink-soft">
                {['차량번호', '기사', '차주', '소속', '편성 종료일', '카드', '카드사', '카드번호', '상태', ''].map((h) => (
                  <th key={h} className="whitespace-nowrap bg-white px-2 py-1.5">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {held.length === 0 && <tr><td colSpan={10} className="px-2 py-6 text-center text-ink-faint">{data ? '정리할 카드가 없습니다.' : '불러오는 중…'}</td></tr>}
              {held.map((r) => {
                const s = heldState(r);
                const key = `${r.car_alloc_id}-${r.kind}`;
                return (
                  <tr key={key} className="border-t border-line">
                    <td className="whitespace-nowrap px-2 py-1">{dash(r.car_no)}</td>
                    <td className="whitespace-nowrap px-2 py-1">{dash(r.driver_name)}</td>
                    <td className="whitespace-nowrap px-2 py-1">{dash(r.owner_name)}</td>
                    <td className="whitespace-nowrap px-2 py-1">{dash(r.grp_name)}</td>
                    <td className="whitespace-nowrap px-2 py-1">{r.enddt}</td>
                    <td className="whitespace-nowrap px-2 py-1">{KIND[r.kind]}</td>
                    <td className="whitespace-nowrap px-2 py-1">{dash(r.corp)}</td>
                    <td className="whitespace-nowrap px-2 py-1 font-mono text-xs">{r.num}</td>
                    <td className={`px-2 py-1 text-xs ${s.tone}`}>{s.label}</td>
                    <td className="whitespace-nowrap px-2 py-1">
                      {canWrite && (
                        <span className="flex gap-1">
                          {r.move_to && <button className={btn} disabled={busy === key} onClick={() => void release(r, r.move_to)}>새 편성으로 넘기기</button>}
                          <button className={btn} disabled={busy === key} onClick={() => void release(r, null)}>회수 처리</button>
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {view === 'usage' && (
          <table className="w-full text-sm">
            <thead className="sticky top-0">
              <tr className="text-left text-xs text-ink-soft">
                {['차량번호', '기사', '차주', '계정', '금액', '내용', '맞지 않는 이유'].map((h) => (
                  <th key={h} className="whitespace-nowrap bg-white px-2 py-1.5">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {usage.length === 0 && <tr><td colSpan={7} className="px-2 py-6 text-center text-ink-faint">{data ? '편성과 맞지 않는 줄이 없습니다.' : '불러오는 중…'}</td></tr>}
              {usage.map((r) => (
                <tr key={r.result_daily_id} className="border-t border-line">
                  <td className="whitespace-nowrap px-2 py-1">{dash(r.car_no)}</td>
                  <td className="whitespace-nowrap px-2 py-1">{dash(r.driver_name)}</td>
                  <td className="whitespace-nowrap px-2 py-1">{dash(r.owner_name)}</td>
                  <td className="whitespace-nowrap px-2 py-1">{r.account_name}</td>
                  <td className="whitespace-nowrap px-2 py-1 text-right">{money(r.amt)}</td>
                  <td className="max-w-[16rem] truncate px-2 py-1 text-xs" title={r.comment ?? ''}>{dash(r.comment)}</td>
                  <td className="whitespace-nowrap px-2 py-1 text-xs text-amber-800">{r.st}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {view === 'live' && (
          <table className="w-full text-sm">
            <thead className="sticky top-0">
              <tr className="text-left text-xs text-ink-soft">
                {['차량번호', '기사', '기사구분', '차주', '소속', '편성 시작일', '주유카드사', '주유카드 번호', '하이패스사', '하이패스 번호'].map((h) => (
                  <th key={h} className="whitespace-nowrap bg-white px-2 py-1.5">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {live.length === 0 && <tr><td colSpan={10} className="px-2 py-6 text-center text-ink-faint">{data ? '해당하는 카드가 없습니다.' : '불러오는 중…'}</td></tr>}
              {live.map((r) => (
                <tr key={r.car_alloc_id} className="border-t border-line">
                  <td className="whitespace-nowrap px-2 py-1">{dash(r.car_no)}</td>
                  <td className="whitespace-nowrap px-2 py-1">{dash(r.driver_name)}</td>
                  <td className="whitespace-nowrap px-2 py-1">{dash(kindLabel(r.driver_kind))}</td>
                  <td className="whitespace-nowrap px-2 py-1">{dash(r.owner_name)}</td>
                  <td className="whitespace-nowrap px-2 py-1">{dash(r.grp_name)}</td>
                  <td className="whitespace-nowrap px-2 py-1">{dash(r.startdt)}</td>
                  <td className="whitespace-nowrap px-2 py-1">{dash(r.fuel_corp)}</td>
                  <td className="whitespace-nowrap px-2 py-1 font-mono text-xs">{dash(r.fuel_num)}</td>
                  <td className="whitespace-nowrap px-2 py-1">{dash(r.hipass_corp)}</td>
                  <td className="whitespace-nowrap px-2 py-1 font-mono text-xs">{dash(r.hipass_num)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </FitBox>
    </div>
  );
}
